import { useState } from "react";

import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Pagination,
  Link,
  Chip,
  Stack,
  useTheme,
  Button,
} from "@mui/material";

import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";
import DangerousIcon from "@mui/icons-material/Dangerous";
import AddIcon from "@mui/icons-material/Add";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

import { Link as RouterLink, useNavigate } from "react-router-dom";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";

import { capitalize, pickBy, sortBy, startCase, toPairs } from "lodash";

import { tzAwareHumanizeTimeString } from "../../utilities/formatting";

import { APIFraudSearch } from "../../types/APIFraudSearch";
import { useTrueBizApi } from "../../api";

const columnHelper = createColumnHelper<APIFraudSearch>();

export interface Props {
  pageSize?: number;
}

export default function FraudSearchList({ pageSize = 10 }: Props) {
  const api = useTrueBizApi();

  const theme = useTheme();
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });

  const listQuery = useQuery({
    queryFn: async () => {
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1_200 + Math.random() * 750);
      });

      const result = await api.listFraudSearches({
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      });

      return result;
    },
    queryKey: ["listFraudSearches", pagination.pageIndex, pagination.pageSize],
    placeholderData: keepPreviousData,
    refetchInterval: 15_000, // refresh every 15 sec
  });

  const table = useReactTable({
    data: listQuery.data?.searches || [],
    state: {
      pagination,
    },
    columns: [
      columnHelper.accessor("created_at", {
        header: () => <span>Searched at</span>,
        cell: (info) => {
          if (!info.getValue()) return "—";

          return tzAwareHumanizeTimeString(info.getValue());
        },
      }),
      columnHelper.accessor("status", {
        header: () => <span>Current status</span>,
        cell: (info) => {
          const { status, status_description } = info.row.original;

          const formattedName = startCase(status.toLowerCase());

          if (info.row.original.status === "PROCESSING") {
            return (
              <Stack direction="row">
                <HourglassEmptyIcon fontSize="small" />
                {formattedName}
                {status_description ? `: ${status_description}` : ""}
              </Stack>
            );
          }

          return formattedName;
        },
      }),
      columnHelper.accessor("query", {
        header: () => <span>Search</span>,
        cell: (info) => {
          if (!info.getValue()) return "—";

          const searchInputs = sortBy(toPairs(pickBy(info.getValue())), [
            (d) => d[0],
          ]);

          if (!searchInputs.length) return "—";

          if (
            info.row.original.status === "FINISHED" &&
            info.row.original.result?.business_name
          ) {
            // if we have a result show the business name
            return info.row.original.result.business_name;
          }

          return (
            <Stack gap="0.5em" direction="row">
              {searchInputs.map(([key, value]) => {
                const name = capitalize(key.replaceAll("_", " "));
                return (
                  <Chip
                    key={`${info.row.original.id}::query::${key}`}
                    size="small"
                    variant="outlined"
                    label={
                      <>
                        <strong>{name}</strong>: {value}
                      </>
                    }
                    sx={{
                      color: "currentcolor",
                    }}
                  />
                );
              })}
            </Stack>
          );
        },
      }),
      columnHelper.accessor("result", {
        header: () => <span>Fraud Risk</span>,
        cell: (info) => {
          if (!info.getValue()) return "—";

          if (info.row.original.status !== "FINISHED") return "—";

          const fraudRisk = info.row.original.result?.fraud_likelihood_risk;
          if (!fraudRisk) return "—";

          switch (fraudRisk) {
            case "HIGH":
              return (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: "0.25em",
                    color: theme.palette.error.light,
                  }}
                >
                  <DangerousIcon /> High
                </Stack>
              );
            case "MEDIUM":
              return (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: "0.25em",
                    color: theme.palette.warning.light,
                  }}
                >
                  <WarningIcon /> Medium
                </Stack>
              );
            case "LOW":
              return (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: "0.25em",
                    color: theme.palette.success.light,
                  }}
                >
                  <VerifiedIcon /> Low
                </Stack>
              );
            default: {
              console.error(`Unexpected fraud risk: ${fraudRisk}`);
              return fraudRisk;
            }
          }
        },
      }),
    ],

    getCoreRowModel: getCoreRowModel(),

    manualPagination: true,
    onPaginationChange: setPagination,
  });

  return (
    <Box sx={{ pb: 3 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h4" component="h1" fontWeight={700}>
          Propensity to Fraud Searches
          {(listQuery.isLoading || listQuery.isFetching) && (
            <CircularProgress size={28} sx={{ marginLeft: 1 }} />
          )}
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            display="block"
            onClick={() => {
              navigate("/propensity-to-fraud/search");
            }}
          >
            New search
          </Button>
        </Box>
      </Stack>

      <Paper elevation={2} sx={{ p: 0 }}>
        <TableContainer component={Box} sx={{ marginTop: 3 }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {table.getFlatHeaders().map((header) => {
                  return (
                    <TableCell key={header.id}>
                      <strong>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </strong>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      cursor:
                        row.original.status === "FINISHED"
                          ? "pointer"
                          : row.original.status === "PROCESSING"
                          ? "progress"
                          : undefined,
                      color:
                        row.original.status === "PROCESSING"
                          ? theme.palette.grey[600]
                          : undefined,
                    }}
                    onClick={() => {
                      if (row.original.status !== "FINISHED") return;
                      if (!row.original.id) return;

                      navigate(`/propensity-to-fraud/${row.original.id}`);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell
                          key={cell.id}
                          sx={{
                            color: "unset",
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {!listQuery.isLoading &&
                listQuery.data?.searches?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      No searches found.{" "}
                      <Link
                        component={RouterLink}
                        to="/propensity-to-fraud/search"
                      >
                        Start a new search...
                      </Link>
                    </TableCell>
                  </TableRow>
                )}
              {listQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={5}>Loading searches...</TableCell>
                </TableRow>
              )}
              {listQuery.isError && (
                <TableRow>
                  <TableCell colSpan={5}>
                    There was a problem loading searches.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {(listQuery?.data?.count || 0) > pagination.pageSize && (
        <Box
          display="flex"
          justifyContent="center"
          paddingTop={3}
          paddingBottom={2}
        >
          <Pagination
            siblingCount={3}
            count={
              listQuery.data
                ? Math.ceil(listQuery.data.count / listQuery.data.limit)
                : undefined
            }
            page={pagination.pageIndex + 1}
            onChange={(_: React.ChangeEvent<unknown>, value: number) => {
              table.setPageIndex(Math.max(value - 1, 0));
            }}
            showFirstButton={
              listQuery.data
                ? Math.ceil(listQuery.data.count / listQuery.data.limit) > 3
                : false
            }
            showLastButton={
              listQuery.data
                ? Math.ceil(listQuery.data.count / listQuery.data.limit) > 3
                : false
            }
          />
        </Box>
      )}
    </Box>
  );
}
